import { Injectable } from "@angular/core";
import { signalStore, withState, patchState } from "@ngrx/signals";
import { ProductService } from "../service/product-service";
import { UserService } from "../service/user-service";
import { UserNoteService } from "../service/userNote.service";
import { SIGN_IN_STATUS, UserProfile } from "../utils/commonTypes";
import { Hub } from "@aws-amplify/core";
import { getCurrentUser } from '@aws-amplify/auth';

type UserState = {
    profile: UserProfile | null;

    isSigningIn: boolean;
    isSigningOut: boolean;

    isLoading: boolean;
    isUpdating: boolean;
};

const initialState: UserState = {
    profile: null,

    isSigningIn: false,
    isSigningOut: false,
    isLoading: false,
    isUpdating: false,

};

@Injectable({
    providedIn: 'root'
  })
export class UserStore extends signalStore(
    { protectedState: false, providedIn: 'root' },
    withState(initialState)
) {
    constructor(private service: UserService, private productService: ProductService, private userNoteService: UserNoteService) {
        super();
        this.listenAuthEvents();
    }

    async resetState() {
        patchState(this, { isLoading: false, profile: null });
    }

    async signIn(name: string, password: string): Promise<SIGN_IN_STATUS> {
        patchState(this, { isSigningIn: true });

        const result = await this.service.signIn(name, password);
        if (result === SIGN_IN_STATUS.SUCCESS) {
            await this.loadUser();
            patchState(this, { profile: this.service.getUserProfile(), isSigningIn: false });
        } else {
            patchState(this, { isSigningIn: false });
        }
        return result;
    }

    async signOut() {
        patchState(this, { isSigningOut: true });
        await this.service.signOut();
    }

    async loadUser(forceReload:boolean=false): Promise<boolean> {
        const profile = this.service.getUserProfile();
        if (profile && !forceReload) { return true;}

        const tokenProfile = await this.service.extractUserProfileFromToken();
        /** the token profile already has the user's name, e-mail and avatar url */
        if(!tokenProfile){ return false; }

        patchState(this, { isLoading: true });

        //Let's try to load the remaining user profile data from the DB
        const dbProfile = await this.service.loadUser(tokenProfile.id);
        if(!dbProfile){
            console.log(`User profile not found in the DB, that's serious!`);
            patchState(this, { isLoading: false, profile: tokenProfile });
        }else{
            //Trick to force the image to reload
            if(dbProfile.avatar && dbProfile.avatar.trim().length>0){
                dbProfile.avatar =  `${dbProfile.avatar}?cb=${Date.now()}`;
            }
            patchState(this, { isLoading: false, profile: dbProfile });
        }
        return true;
    }

    async updateUser(props:{name?:string,avatarUrl?:string,about?:string,phone?:string}): Promise<boolean> {
        const profile = this.profile();
        if (profile != null) {
            patchState(this, { isUpdating: true });
            const success = await this.service.updateUser({...props,userId:profile.id});
            if (success) {
                const name=props.name?props.name:profile.name;
                var avatarUrl=props.avatarUrl?props.avatarUrl:profile.avatar;
                
                //Trick to force the image to reload
                if(avatarUrl && avatarUrl.trim().length>0){
                    avatarUrl =  `${avatarUrl}?cb=${Date.now()}`;
                }

                const about=props.about?props.about:profile.about;
                const phone=props.phone?props.phone:profile.phone;
                patchState(this, { isUpdating: false, profile: { ...profile, name:name, avatar:avatarUrl, about:about, phone:phone } });
                
                return true;
            } else {
                patchState(this, { isUpdating: false });
            }
        }
        return false;
    }

    async removeAvatar(){
        const profile = this.profile();
        if(!profile){return;}
        
        await this.service.deleteAvatar();
        patchState(this, { isUpdating: false, profile: { ...profile, avatar:'', } });
    }


    public async isAuthenticated(){
        try{
            const user = await getCurrentUser();
            return true;
        }catch(e){
            return false;
        }
    }

    private listenAuthEvents(){
        Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
              case 'signedIn':
                console.log('user have been signedIn successfully.', payload);
                this.loadUser();
                break;
              case 'signedOut':
                this.resetState();
                console.log('user have been signedOut successfully.');
                break;
              case 'tokenRefresh':
                //update user profile
                console.log('auth tokens have been refreshed automatically.');
                
                this.loadUser(true).then(()=>{
                  console.log(`new user profile data has been updated from the token`);    
                })
                break;
              case 'tokenRefresh_failure':
                console.log('failure while refreshing auth tokens.');
                break;
              case 'signInWithRedirect':
                console.log('signInWithRedirect API has successfully been resolved.', payload);
                break;
              case 'signInWithRedirect_failure':
                console.log('failure while trying to resolve signInWithRedirect API.');
                break;
            }
          });
    }    
}